import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Truck,
  MapPin,
  Phone,
  CheckCircle,
  Clock,
  Package,
  Navigation,
  User,
  MessageCircle,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface TrackingData {
  delivery: {
    id: string;
    status: string;
    progress: number;
    driver: {
      name: string;
      phone: string;
      photoUrl: string | null;
      vehicle: string | null;
      licensePlate: string | null;
    } | null;
    location: {
      latitude: number;
      longitude: number;
    } | null;
    etaMinutes: number | null;
    timestamps: {
      created: string;
      pickedUp: string | null;
      arrived: string | null;
      delivered: string | null;
    };
    proofOfDeliveryUrl: string | null;
  };
  order: {
    number: string;
    customerName: string;
    destination: {
      address: string;
      city: string;
      state: string;
      zip: string;
    };
    instructions: string | null;
  };
  timeline: Array<{
    id: string;
    type: string;
    status: string;
    description: string | null;
    location: {
      latitude: number;
      longitude: number;
      address: string | null;
    } | null;
    timestamp: string;
  }>;
}

async function fetchTracking(deliveryId: string): Promise<TrackingData> {
  const response = await fetch(`/api/tracking/${deliveryId}`);
  if (!response.ok) throw new Error('Tracking not found');
  return response.json();
}

function StatusIndicator({ status }: { status: string }) {
  const statusConfig: Record<
    string,
    { label: string; color: string; icon: React.ReactNode }
  > = {
    PENDING: {
      label: 'Finding Driver',
      color: 'text-yellow-500',
      icon: <Clock className="w-6 h-6" />,
    },
    DRIVER_ASSIGNED: {
      label: 'Driver Assigned',
      color: 'text-blue-500',
      icon: <User className="w-6 h-6" />,
    },
    EN_ROUTE_TO_PICKUP: {
      label: 'Heading to Pickup',
      color: 'text-blue-500',
      icon: <Navigation className="w-6 h-6" />,
    },
    ARRIVED_AT_PICKUP: {
      label: 'At Pickup Location',
      color: 'text-purple-500',
      icon: <MapPin className="w-6 h-6" />,
    },
    PICKED_UP: {
      label: 'Package Picked Up',
      color: 'text-purple-500',
      icon: <Package className="w-6 h-6" />,
    },
    EN_ROUTE_TO_DROPOFF: {
      label: 'On the Way',
      color: 'text-uber-green',
      icon: <Truck className="w-6 h-6 animate-pulse" />,
    },
    ARRIVED_AT_DROPOFF: {
      label: 'Driver Arrived!',
      color: 'text-uber-green',
      icon: <MapPin className="w-6 h-6" />,
    },
    DELIVERED: {
      label: 'Delivered',
      color: 'text-green-600',
      icon: <CheckCircle className="w-6 h-6" />,
    },
  };

  const config = statusConfig[status] || statusConfig.PENDING;

  return (
    <div className={`flex items-center gap-2 ${config.color}`}>
      {config.icon}
      <span className="font-semibold text-lg">{config.label}</span>
    </div>
  );
}

function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className="bg-uber-green h-2.5 rounded-full transition-all duration-500"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

function TimelineItem({
  event,
  isLast,
}: {
  event: TrackingData['timeline'][0];
  isLast: boolean;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-3 h-3 bg-uber-green rounded-full" />
        {!isLast && <div className="w-0.5 h-full bg-gray-200 my-1" />}
      </div>
      <div className="pb-4">
        <p className="font-medium">{event.status.replace(/_/g, ' ')}</p>
        {event.description && (
          <p className="text-sm text-gray-600">{event.description}</p>
        )}
        {event.location?.address && (
          <p className="text-sm text-gray-500">{event.location.address}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">
          {format(new Date(event.timestamp), 'h:mm a')}
        </p>
      </div>
    </div>
  );
}

export default function TrackDeliveryDetailed() {
  const { deliveryId } = useParams<{ deliveryId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tracking', deliveryId],
    queryFn: () => fetchTracking(deliveryId!),
    refetchInterval: 10000, // Refresh every 10 seconds
    enabled: !!deliveryId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-uber-green mx-auto" />
          <p className="mt-4 text-gray-600">Loading tracking info...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Tracking Not Available</h1>
          <p className="text-gray-600">
            This delivery tracking link may be invalid or expired.
          </p>
        </div>
      </div>
    );
  }

  const { delivery, order, timeline } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-uber-green text-white">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Truck className="w-6 h-6" />
              <span className="font-medium">Order {order.number}</span>
            </div>
            {delivery.etaMinutes && delivery.status !== 'DELIVERED' && (
              <div className="text-right">
                <p className="text-sm opacity-80">ETA</p>
                <p className="text-xl font-bold">{delivery.etaMinutes} min</p>
              </div>
            )}
          </div>

          <StatusIndicator status={delivery.status} />

          <div className="mt-4">
            <ProgressBar progress={delivery.progress} />
          </div>
        </div>
      </div>

      {/* Map Placeholder */}
      <div className="bg-gray-200 h-48 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Live map tracking</p>
            {delivery.location && (
              <p className="text-xs mt-1">
                Last update:{' '}
                {formatDistanceToNow(new Date(), { addSuffix: true })}
              </p>
            )}
          </div>
        </div>
        {/* In production, integrate with Google Maps or Mapbox */}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Driver Card */}
        {delivery.driver && (
          <div className="card">
            <h3 className="font-semibold mb-4">Your Driver</h3>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                {delivery.driver.photoUrl ? (
                  <img
                    src={delivery.driver.photoUrl}
                    alt={delivery.driver.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-lg">{delivery.driver.name}</p>
                {delivery.driver.vehicle && (
                  <p className="text-gray-600">
                    {delivery.driver.vehicle}
                    {delivery.driver.licensePlate &&
                      ` - ${delivery.driver.licensePlate}`}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <a
                  href={`tel:${delivery.driver.phone}`}
                  className="p-3 bg-uber-green text-white rounded-full hover:bg-green-600"
                >
                  <Phone className="w-5 h-5" />
                </a>
                <a
                  href={`sms:${delivery.driver.phone}`}
                  className="p-3 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Destination */}
        <div className="card">
          <h3 className="font-semibold mb-4">Delivery Destination</h3>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-uber-green mt-0.5" />
            <div>
              <p className="font-medium">{order.customerName}</p>
              <p className="text-gray-600">{order.destination.address}</p>
              <p className="text-gray-600">
                {order.destination.city}, {order.destination.state}{' '}
                {order.destination.zip}
              </p>
            </div>
          </div>
          {order.instructions && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm font-medium text-yellow-800">
                Delivery Instructions:
              </p>
              <p className="text-sm text-yellow-700">{order.instructions}</p>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="card">
          <h3 className="font-semibold mb-4">Delivery Timeline</h3>
          <div className="space-y-0">
            {timeline.map((event, index) => (
              <TimelineItem
                key={event.id}
                event={event}
                isLast={index === timeline.length - 1}
              />
            ))}
          </div>
        </div>

        {/* Proof of Delivery */}
        {delivery.proofOfDeliveryUrl && (
          <div className="card">
            <h3 className="font-semibold mb-4">Proof of Delivery</h3>
            <img
              src={delivery.proofOfDeliveryUrl}
              alt="Proof of Delivery"
              className="w-full rounded-lg"
            />
          </div>
        )}

        {/* Timestamps */}
        <div className="card">
          <h3 className="font-semibold mb-4">Delivery Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Order Created</span>
              <span>
                {format(
                  new Date(delivery.timestamps.created),
                  'MMM d, h:mm a'
                )}
              </span>
            </div>
            {delivery.timestamps.pickedUp && (
              <div className="flex justify-between">
                <span className="text-gray-600">Picked Up</span>
                <span>
                  {format(
                    new Date(delivery.timestamps.pickedUp),
                    'MMM d, h:mm a'
                  )}
                </span>
              </div>
            )}
            {delivery.timestamps.delivered && (
              <div className="flex justify-between">
                <span className="text-gray-600">Delivered</span>
                <span>
                  {format(
                    new Date(delivery.timestamps.delivered),
                    'MMM d, h:mm a'
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
